import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BasecampLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Btn, Modal, Input } from '../../components/UI';
import { api, getUser } from '../../utils/api';
import { AssistantText } from '../../components/AssistantDock';

const PAID_NOTE = 'The Hansen Beck assistant is a paid feature — access is granted on request.';
const canManage = (u) => {
  const r = u?.roles || [], f = u?.funcs || [];
  return r.includes('SUPERADMIN') || r.includes('ADM') || r.includes('SH') || f.includes('System Administrator');
};
function Loading() { return <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><Spinner /></div>; }

// Admin panel — where a superadmin/admin approves assistant-access requests.
function AccessRequests() {
  const [reqs, setReqs] = useState(null);
  const load = useCallback(() => { api.ai.assistantRequests().then(d => setReqs(d.requests)).catch(() => setReqs([])); }, []);
  useEffect(() => { load(); }, [load]);
  async function decide(userId, decision) { try { await api.ai.decideAssistant(userId, decision); load(); } catch (e) { alert(e.message); } }
  if (!reqs || reqs.length === 0) return null;
  return (
    <Card style={{ padding: '16px 20px', marginBottom: 18, borderLeft: '3px solid var(--gold, #C4872B)' }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.02rem', marginBottom: 4 }}>Assistant access requests</h3>
      <p style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginBottom: 10 }}>People who requested the (paid) Hansen Beck assistant. Grant or deny below.</p>
      {reqs.map(r => (
        <div key={r.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--line)', fontSize: '0.88rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}><b>{r.name || r.email}</b>{r.email && r.name ? <span style={{ color: 'var(--ink-faint)', fontSize: '0.8rem' }}> · {r.email}</span> : null}</div>
          <Btn size="sm" onClick={() => decide(r.userId, 'grant')}>Grant</Btn>
          <Btn size="sm" variant="ghost" onClick={() => decide(r.userId, 'deny')} style={{ color: 'var(--danger)' }}>Deny</Btn>
        </div>
      ))}
    </Card>
  );
}

// Admin control — regenerate the shared knowledge snapshot from the DB.
function KnowledgeBase() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.ai.assistantKnowledge().then(setInfo).catch(() => setInfo({ generatedAt: null })); }, []);
  useEffect(() => { load(); }, [load]);
  async function rebuild() { setBusy(true); try { await api.ai.rebuildAssistantKnowledge(); load(); } catch (e) { alert(e.message); } finally { setBusy(false); } }
  return (
    <Card style={{ padding: '14px 20px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.98rem' }}>Assistant knowledge base</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>
          The shared digest (profiles, facet library, Units, method) the assistant starts every chat from.
          {info && info.generatedAt ? ` Last built ${new Date(info.generatedAt).toLocaleString()}.` : ' Not built yet — rebuild after you change profiles or Units.'}
        </div>
      </div>
      <Btn size="sm" variant="outline" loading={busy} onClick={rebuild}>Refresh knowledge</Btn>
    </Card>
  );
}

export default function Assistant() {
  const admin = canManage(getUser());
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const scrollRef = useRef(null);

  const refreshConversations = useCallback(() => api.ai.assistantConversations().then(d => { setConversations(d.conversations); return d.conversations; }).catch(() => []), []);

  const selectConversation = useCallback((id) => {
    setActiveId(id); setMessages([]); setLoadingMsgs(true);
    api.ai.assistantMessages(id).then(d => setMessages(d.messages || [])).catch(() => setMessages([])).finally(() => setLoadingMsgs(false));
  }, []);

  useEffect(() => {
    api.ai.assistantStatus().then(d => {
      setStatus(d.status);
      if (d.status === 'granted') refreshConversations().then(list => { if (list.length) selectConversation(list[0].id); });
    }).catch(() => setStatus('none'));
  }, [refreshConversations, selectConversation]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, thinking]);

  async function requestAccess() {
    setBusy(true);
    try { await api.ai.requestAssistant(); setStatus('requested'); } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  function newChat() { setActiveId(null); setMessages([]); setInput(''); }
  function startRename(c, e) { e.stopPropagation(); setEditingId(c.id); setEditTitle(c.title || ''); }
  async function saveRename(id) {
    const t = editTitle.trim();
    setEditingId(null);
    if (!t) return;
    try { await api.ai.renameAssistantConversation(id, t); refreshConversations(); } catch (err) { alert(err.message); }
  }
  async function deleteConversation(id, e) {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation? Its messages are removed.')) return;
    try {
      await api.ai.deleteAssistantConversation(id);
      const list = await refreshConversations();
      if (id === activeId) { if (list.length) selectConversation(list[0].id); else newChat(); }
    } catch (err) { alert(err.message); }
  }
  async function send() {
    const q = input.trim();
    if (!q || thinking) return;
    setMessages(m => [...m, { role: 'user', content: q }]);
    setInput(''); setThinking(true);
    try {
      const r = await api.ai.assistantChat(q, activeId);
      setMessages(m => [...m, { role: 'assistant', content: r.answer }]);
      if (r.conversationId && r.conversationId !== activeId) { setActiveId(r.conversationId); }
      refreshConversations();
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: `Sorry — I couldn’t answer that just now (${e.message}).` }]);
    } finally { setThinking(false); }
  }

  if (status === null) return <BasecampLayout><PageHeader title="Hansen Beck assistant" subtitle="Your AI guide" /><Loading /></BasecampLayout>;

  // ── Not granted — request access (admins still see approvals) ──────────────
  if (status !== 'granted') {
    return (
      <BasecampLayout>
        <PageHeader title="Hansen Beck assistant" subtitle="Your AI guide inside Basecamp" />
        {admin && <><KnowledgeBase /><AccessRequests /></>}
        <Card style={{ padding: '24px 26px', maxWidth: 620 }}>
          <p style={{ fontSize: '0.95rem', color: 'var(--ink-soft)', marginBottom: 14 }}>The Hansen Beck assistant answers questions about your work — your projects, what’s due, the method, the next step to take. It only reads; it never changes or deletes anything.</p>
          <Alert type="warn">{PAID_NOTE}</Alert>
          {status === 'requested' ? <div style={{ marginTop: 14 }}><Alert type="info">Your request has been sent — an administrator will grant access.</Alert></div>
            : status === 'denied' ? <div style={{ marginTop: 14 }}><Alert type="error">Your previous request was not granted.</Alert><Btn loading={busy} onClick={requestAccess} style={{ marginTop: 12 }}>Request access again</Btn></div>
            : <Btn loading={busy} onClick={requestAccess} style={{ marginTop: 16 }}>Request access to the assistant</Btn>}
        </Card>
      </BasecampLayout>
    );
  }

  // ── Granted — conversations + chat ─────────────────────────────────────────
  return (
    <BasecampLayout>
      <PageHeader title="Hansen Beck assistant" subtitle="Ask about your work — read-only guidance" />
      {admin && <><KnowledgeBase /><AccessRequests /></>}
      <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginBottom: 12 }}>{PAID_NOTE} · Read-only. Chats are saved; start a new one to keep contexts (and cost) small.</p>

      <div style={{ display: 'flex', gap: 14, alignItems: 'stretch', height: '64vh', minHeight: 400 }}>
        {/* Conversations pane */}
        <Card style={{ width: 230, flexShrink: 0, padding: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Btn size="sm" onClick={newChat} style={{ marginBottom: 10 }}>+ New chat</Btn>
          <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {conversations.length === 0 && <div style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', padding: '6px 4px' }}>No conversations yet.</div>}
            {conversations.map(c => (
              <div key={c.id} onClick={() => selectConversation(c.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 10px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                  background: c.id === activeId ? 'var(--canvas-warm)' : 'transparent' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: '0.84rem', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title || 'New chat'}</div>
                <button onClick={(e) => startRename(c, e)} title="Rename" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '0.82rem' }}>✎</button>
                <button onClick={(e) => deleteConversation(c.id, e)} title="Delete" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '0.9rem' }}>✕</button>
              </div>
            ))}
          </div>
        </Card>

        {/* Chat pane */}
        <Card style={{ flex: 1, minWidth: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {loadingMsgs ? <Loading /> : messages.length === 0 ? (
              <div style={{ color: 'var(--ink-faint)', fontSize: '0.9rem', margin: 'auto', textAlign: 'center', maxWidth: 420 }}>
                {activeId ? 'No messages in this chat yet.' : 'New chat. Ask me things like'} <i>“what’s my next step on the Acme project?”</i>, <i>“which profile should I refresh?”</i>, or <i>“how does Gate 2 work?”</i>
              </div>
            ) : messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: 3, textAlign: m.role === 'user' ? 'right' : 'left' }}>{m.role === 'user' ? 'You' : 'Hansen Beck assistant'}</div>
                <div style={{ background: m.role === 'user' ? 'var(--ink)' : 'var(--canvas-warm)', color: m.role === 'user' ? '#fff' : 'var(--ink)', padding: '10px 14px', borderRadius: 'var(--radius-md)', fontSize: '0.9rem', lineHeight: 1.55, whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal' }}>{m.role === 'user' ? m.content : <AssistantText text={m.content} />}</div>
              </div>
            ))}
            {thinking && <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-soft)', fontSize: '0.85rem' }}><Spinner size={16} /> thinking…</div>}
          </div>
          <div style={{ borderTop: '1px solid var(--line)', padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask the Hansen Beck assistant…" rows={2}
              style={{ flex: 1, resize: 'none', fontFamily: 'var(--font-body)', fontSize: '0.9rem', padding: '9px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)' }} />
            <Btn onClick={send} loading={thinking} disabled={!input.trim()}>Send</Btn>
          </div>
        </Card>
      </div>

      {editingId !== null && (
        <Modal open onClose={() => setEditingId(null)} title="Rename conversation">
          <Input value={editTitle} autoFocus onChange={e => setEditTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveRename(editingId); }} placeholder="Conversation name" />
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Btn onClick={() => saveRename(editingId)} disabled={!editTitle.trim()}>Confirm</Btn>
            <Btn variant="ghost" onClick={() => setEditingId(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </BasecampLayout>
  );
}
